import { useEffect, useRef } from 'react';
import { findBlock, updateBlockInTree } from '../utils/blockUtils';
import {
  clearPendingDocVideoFile,
  registerDocVideoUploadStarter,
  rememberPendingDocVideoFile,
} from '../utils/docVideoUploadBridge';
import { uploadDocVideo } from '../../../services/docVideoService';

/**
 * Registers the upload starter for this editor instance and patches video
 * blocks as uploads progress (local → uploading → ready | error).
 * Failed uploads keep the File in the bridge so the block can Retry.
 */
export const useDocVideoUpload = (docId, blocksRef, commit) => {
  const docIdRef = useRef(docId);
  docIdRef.current = docId;
  const inFlightRef = useRef(new Set());

  useEffect(() => {
    const patchProps = (blockId, propsPatch) => {
      const current = blocksRef.current;
      const block = findBlock(current, blockId);
      if (!block || block.type !== 'video') return;
      const nextProps = { ...block.props, ...propsPatch };
      commit(updateBlockInTree(current, blockId, { props: nextProps }), {
        record: false,
      });
    };

    const startUpload = async (blockId, file) => {
      if (!blockId || !file) return;
      if (inFlightRef.current.has(blockId)) return;
      inFlightRef.current.add(blockId);
      rememberPendingDocVideoFile(blockId, file);

      const id = docIdRef.current;
      if (!id) {
        patchProps(blockId, {
          mode: 'upload',
          uploadStatus: 'error',
          errorMessage: 'Save the doc before adding videos',
        });
        inFlightRef.current.delete(blockId);
        return;
      }

      patchProps(blockId, {
        mode: 'upload',
        uploadStatus: 'uploading',
        uploadProgress: 0,
        errorMessage: undefined,
      });

      try {
        let lastPct = -1;
        const result = await uploadDocVideo(id, file, (pct) => {
          const nextPct = Math.round(Math.min(100, Math.max(0, pct)));
          if (nextPct === lastPct) return;
          lastPct = nextPct;
          patchProps(blockId, {
            uploadStatus: 'uploading',
            uploadProgress: nextPct,
          });
        });
        const block = findBlock(blocksRef.current, blockId);
        const localUrl = block?.props?.localPreviewUrl;
        if (localUrl?.startsWith('blob:')) {
          try {
            URL.revokeObjectURL(localUrl);
          } catch {
            /* ignore */
          }
        }
        clearPendingDocVideoFile(blockId);
        patchProps(blockId, {
          mode: 'upload',
          publicUrl: result.publicUrl,
          videoId: result.videoId,
          r2Key: result.r2Key,
          fileName: result.fileName,
          mimeType: result.mimeType,
          size: result.size,
          uploadStatus: 'ready',
          uploadProgress: undefined,
          localPreviewUrl: undefined,
          errorMessage: undefined,
          embedUrl: undefined,
          originalUrl: undefined,
          provider: undefined,
        });
      } catch (err) {
        console.warn('Doc video upload failed:', err?.message || err);
        const msg =
          typeof err?.message === 'string' &&
          /storage limit|too large|MP4|WebM|not found|Save the doc/i.test(err.message)
            ? err.message
            : 'Upload failed';
        patchProps(blockId, {
          mode: 'upload',
          uploadStatus: 'error',
          uploadProgress: undefined,
          errorMessage: msg,
        });
      } finally {
        inFlightRef.current.delete(blockId);
      }
    };

    registerDocVideoUploadStarter(startUpload);
    return () => registerDocVideoUploadStarter(null);
  }, [blocksRef, commit]);
};
